<template>
  <BaseModal
    :isOpen="open"
    maxWidth="5xl"
    contentPadding="none"
    :closeOnBackdrop="!isSubmitting"
    @close="closeModal(true)"
  >
    <form
      class="relative flex max-h-[calc(100vh-4rem)] flex-col overflow-hidden"
      @submit.prevent="handleSubmit"
    >
      <header
        class="flex flex-col gap-4 border-b border-gray-100 px-6 pb-6 pt-8 sm:flex-row sm:items-start sm:justify-between sm:px-9 sm:pt-10"
      >
        <div>
          <h2 class="text-2xl font-semibold text-primary-dark-text">
            Add Members To Space
          </h2>
          <p class="mt-2 text-sm text-gray-500">
            Choose the members you want to add and review their details.
          </p>
        </div>
      </header>

      <section
        class="grid flex-1 gap-8 overflow-y-auto px-6 pb-8 pt-6 sm:px-9 sm:pb-10 lg:grid-cols-[1.6fr_1fr]"
      >
        <div
          class="flex h-full flex-col overflow-hidden rounded-lg border border-gray-100"
        >
          <div class="border-b border-gray-100 p-5">
            <label
              class="text-sm font-semibold text-primary-dark-text"
              for="member-search"
              >Search for</label
            >
            <div class="relative mt-3">
              <span
                class="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400"
              >
                <svg
                  class="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 21l-4.35-4.35M17.25 10.5a6.75 6.75 0 11-13.5 0 6.75 6.75 0 0113.5 0z"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <input
                id="member-search"
                v-model="searchQuery"
                class="w-full rounded-lg border border-gray-200 bg-gray-50 py-3 pl-12 pr-4 text-sm text-primary-dark-text placeholder:text-gray-400 transition focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Search for name, username, or email"
                type="text"
              />
            </div>
          </div>

          <div class="flex-1 overflow-hidden">
            <ul aria-live="polite" class="h-full overflow-y-auto p-2">
              <li v-for="user in filteredOptions" :key="user.id" class="px-2">
                <div
                  :class="[
                    activeUser && activeUser.id === user.id
                      ? 'bg-primary/5 ring-1 ring-primary/30'
                      : '',
                  ]"
                  class="flex w-full items-center justify-between gap-4 rounded-lg px-4 py-3 transition hover:bg-gray-50"
                >
                  <button
                    class="flex flex-1 items-center gap-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    type="button"
                    @click="setActiveUser(user)"
                  >
                    <div
                      class="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-sm font-semibold text-primary"
                    >
                      <img
                        v-if="user.avatarUrl"
                        :alt="`${user.firstName} ${user.lastName}`"
                        :src="user.avatarUrl"
                        class="h-full w-full object-cover"
                      />
                      <span v-else>{{ getInitials(user) }}</span>
                    </div>
                    <div class="text-left">
                      <p class="text-sm font-semibold text-primary-dark-text">
                        {{ user.firstName }} {{ user.lastName }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ user.email }}
                      </p>
                    </div>
                  </button>
                  <button
                    :class="[
                      isUserSelected(user)
                        ? 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-100'
                        : 'bg-primary/10 text-primary hover:bg-primary/20',
                    ]"
                    class="rounded-full px-5 py-1.5 text-xs font-semibold transition"
                    type="button"
                    @click.stop="handleUserToggle(user)"
                  >
                    {{ isUserSelected(user) ? "Remove" : "Invite" }}
                  </button>
                </div>
              </li>

              <li v-if="filteredOptions.length === 0" class="px-4 py-16">
                <p class="text-center text-sm text-gray-500">
                  No members match your search.
                </p>
              </li>
            </ul>
          </div>

          <p
            v-if="selectedOptionsValidation"
            class="px-5 pb-4 text-sm text-red-500"
          >
            {{ selectedOptionsValidation }}
          </p>

          <p v-else-if="errorMessage" class="px-5 pb-4 text-sm text-red-500">
            {{ errorMessage }}
          </p>
        </div>

        <aside
          class="flex h-full flex-col justify-between rounded-lg border border-gray-100 bg-gray-50 px-6 py-8 text-left sm:px-8 sm:py-10 sm:text-center"
        >
          <div v-if="activeUser" class="w-full">
            <div
              class="mx-auto flex h-28 w-28 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-white sm:h-32 sm:w-32"
            >
              <img
                v-if="activeUser.avatarUrl"
                :alt="`${activeUser.firstName} ${activeUser.lastName}`"
                :src="activeUser.avatarUrl"
                class="h-full w-full object-cover"
              />
              <span v-else class="text-3xl font-semibold text-primary">
                {{ getInitials(activeUser) }}
              </span>
            </div>

            <h3 class="mt-6 text-xl font-semibold text-primary-dark-text">
              {{ activeUser.firstName }} {{ activeUser.lastName }}
            </h3>
            <p class="mt-1 text-sm text-gray-500">
              {{ activeUser.email }}
            </p>

            <dl class="mt-8 space-y-4 text-sm sm:text-left">
              <div class="flex items-center justify-between">
                <dt class="text-gray-500">Status</dt>
                <dd class="font-semibold text-primary-dark-text">
                  {{ activeUser.isActive ? "Active" : "Inactive" }}
                </dd>
              </div>
              <div class="flex items-center justify-between">
                <dt class="text-gray-500">Username</dt>
                <dd class="font-semibold text-primary-dark-text">
                  {{ activeUser.username || "—" }}
                </dd>
              </div>
              <div class="flex items-center justify-between">
                <dt class="text-gray-500">Domain</dt>
                <dd class="font-semibold text-primary-dark-text">
                  {{ activeUser.domainId || "—" }}
                </dd>
              </div>
            </dl>

            <div
              class="mt-8 rounded-lg bg-white px-4 py-3 text-sm text-primary-dark-text shadow-sm"
            >
              <p>
                {{
                  isUserSelected(activeUser)
                    ? "This member is selected to be added."
                    : "Invite this member to the space using the list on the left."
                }}
              </p>
            </div>
          </div>

          <div v-else class="w-full text-left sm:text-center">
            <div
              class="mx-auto flex h-28 w-28 items-center justify-center rounded-full border border-dashed border-gray-300 bg-white sm:h-32 sm:w-32"
            >
              <svg
                class="h-12 w-12 text-gray-300"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4.5v15m7.5-7.5h-15"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <h3 class="mt-6 text-lg font-semibold text-primary-dark-text">
              Select A Member
            </h3>
            <p class="mt-2 text-sm text-gray-500">
              Choose a member from the list to preview their details.
            </p>
          </div>
        </aside>
      </section>

      <footer
        class="flex flex-col gap-4 border-t border-gray-100 px-6 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-9"
      >
        <div class="text-sm text-gray-500">
          {{ selectedOptions.length }}
          {{
            selectedOptions.length === 1
              ? "member selected"
              : "members selected"
          }}
        </div>
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <button
            :disabled="isSubmitting"
            class="rounded-lg border border-gray-200 px-7 py-3 text-sm font-semibold text-primary-dark-text transition hover:bg-gray-50 hover:text-primary"
            type="button"
            @click="closeModal"
          >
            Cancel
          </button>
          <button
            :disabled="isSubmitting"
            class="rounded-lg bg-btn-gradient px-8 py-3 text-sm font-semibold text-white shadow-lg transition hover:opacity-90 disabled:opacity-60"
            type="submit"
          >
            {{ isSubmitting ? "Saving..." : "Save" }}
          </button>
        </div>
      </footer>

      <div
        v-if="isSubmitting"
        class="absolute inset-0 flex items-center justify-center rounded-xl bg-white/60"
      >
        <div class="spinner"></div>
      </div>
    </form>
  </BaseModal>
</template>

<script>
import { getTargetUsers } from "../services/targetUsers";
import { addMemberToSpace, getMembersBySpaceId } from "../services/space";
import BaseModal from "@/components/ui/BaseModal.vue";

export default {
  components: {
    BaseModal,
  },
  data() {
    return {
      searchQuery: "",
      selectedOptions: [],
      options: [],
      selectedOptionsValidation: "",
      errorMessage: "",
      isSubmitting: false,
      activeUser: null,
      targetUsers: [],
      members: [],
    };
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    spaceId: {
      type: Number,
      default: 0,
    },
    spaceNameId: {
      type: String,
      required: true,
    },
  },
  watch: {
    open(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.resetState();
        this.getTargetUsers();
      }
    },
    filteredOptions(newOptions) {
      if (!Array.isArray(newOptions)) {
        return;
      }

      const activeStillVisible = newOptions.some(
        (user) => this.activeUser && user.id === this.activeUser.id
      );

      if (!activeStillVisible) {
        this.activeUser = newOptions[0] || null;
      }
    },
  },
  beforeUnmount() {
    this.resetState();
  },
  methods: {
    resetState() {
      this.searchQuery = "";
      this.selectedOptions = [];
      this.options = [];
      this.selectedOptionsValidation = "";
      this.errorMessage = "";
      this.activeUser = null;
      this.targetUsers = [];
      this.members = [];
    },
    closeModal(force = false) {
      if (this.isSubmitting && !force) {
        return;
      }

      this.$emit("close");
    },
    mapSelectedUsers(selectedOptions, options) {
      const selectedArray = Array.from(selectedOptions);
      const optionsArray = Array.from(options);

      if (selectedArray.length === 0) {
        this.selectedOptionsValidation = "Need to select at least one user.";
        return null;
      }

      this.selectedOptionsValidation = "";

      const optionIds = new Set(optionsArray.map((user) => user.id));

      return selectedArray
        .filter((user) => optionIds.has(user.id))
        .map((user) => ({
          id: user.id,
          email: user.email,
          username: user.username,
          firstName: user.firstName,
          lastName: user.lastName,
          avatarUrl: user.avatarUrl,
          domainId: user.domainId,
          chatSpaceId: user.chatSpaceId,
          isActive: user.isActive,
          chatId: user.chatId,
        }));
    },
    async handleSubmit() {
      const selectedUsers = this.mapSelectedUsers(
        this.selectedOptions,
        this.options
      );

      if (!Array.isArray(selectedUsers) || selectedUsers.length === 0) {
        return;
      }

      try {
        this.isSubmitting = true;
        await addMemberToSpace(this.spaceNameId, selectedUsers);
        this.$emit("spaceAdded");
        this.$emit("close");
        this.$emit("alert", {
          message: "Members successfully added.",
          type: "success",
        });
        this.resetState();
      } catch {
        this.errorMessage =
          "User is not valid, check if email is used in the google chat.";
      } finally {
        this.isSubmitting = false;
      }
    },
    populateOptions() {
      if (Array.isArray(this.targetUsers) && this.targetUsers.length) {
        this.options = this.targetUsers.map((user) => ({
          id: user.id,
          lastName: user.lastName,
          firstName: user.firstName,
          email: user.email,
          username: user.username,
          avatarUrl: user.avatarUrl,
          domainId: user.domainId,
          chatSpaceId: user.chatSpaceId,
          isActive: user.isActive,
          chatId: user.chatId,
        }));
        this.activeUser = this.options[0] || null;
      } else {
        this.options = [];
        this.activeUser = null;
      }
    },
    isUserSelected(user) {
      return this.selectedOptions.some((selected) => selected.id === user.id);
    },
    handleUserToggle(user) {
      const isSelected = this.isUserSelected(user);

      if (isSelected) {
        this.selectedOptions = this.selectedOptions.filter(
          (selected) => selected.id !== user.id
        );
      } else {
        this.selectedOptions = [...this.selectedOptions, user];
      }
    },
    setActiveUser(user) {
      this.activeUser = user;
    },
    async getTargetUsers() {
      if (!this.spaceId || this.spaceId === 0) {
        this.targetUsers = [];
        this.populateOptions();
        return;
      }
      try {
        this.members = await getMembersBySpaceId(this.spaceId);
        let users = await getTargetUsers();
        users = Array.isArray(users) ? users : [];

        this.targetUsers = users.filter((user) => {
          return (
            user.isActive &&
            !this.members.find((member) => member.id === user.id)
          );
        });
        this.populateOptions();
      } catch {
        this.errorMessage =
          "Failed to get target users. Please contact your support.";
      }
    },
    getInitials(user) {
      const first = user?.firstName ? user.firstName[0] : "";
      const last = user?.lastName ? user.lastName[0] : "";
      const initials = `${first}${last}`.trim();
      return initials || "?";
    },
  },
  computed: {
    filteredOptions() {
      if (!this.searchQuery) {
        return this.options;
      }

      const query = this.searchQuery.trim().toLowerCase();

      return this.options.filter((user) => {
        const name = `${user.firstName || ""} ${user.lastName || ""}`;
        const username = user.username || "";
        const email = user.email || "";
        return `${name} ${username} ${email}`.toLowerCase().includes(query);
      });
    },
  },
};
</script>

<style scoped>
.spinner {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  border-width: 3px;
  border-style: solid;
  border-color: rgba(143, 86, 254, 0.35);
  border-top-color: #af56fe;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
